import { isJSONObject, isOrientation, } from '@backstage-components/base';
import { computeId, familyTree, getModuleProps, mergeProps, applyPropsDiff, } from '@backstage/module-tree-shared';
import { arrayToRecord } from './array-to-lookup';
function nodeToPageModule({ config, node, page, all, tree, vars = {}, groupInstanceData, }) {
    vars = Object.assign({}, vars);
    const module = all.modules[node.id];
    const core = all.cores[module.coreId];
    const component = all.components[core.componentId];
    const props = core.componentFieldData;
    if (typeof props !== 'object' || Array.isArray(props) || props === null) {
        throw new Error(`componentFieldData for Module(${node.id}) was not an object.`);
    }
    /*
     * Props are mainly stored in core.componentFieldData, but there are overrides
     * and one "underride" that's not been written yet.
     * -1 "presets" are set before core's props. But overrides can change the preset.
     *  0 core props "componentFieldData"
     *  1 moduleProps which are overrides at the module level
     *  2 staffProps which are overrides at the group level. When we allow groups within
     *    groups, this will be layered more, but those will come through the same object.
     */
    // If this module isn't in a group, then it can have no staff overrides.
    // Currently, we cannot have embedded groups, so this is a safe place to
    // identify the group boundary.
    if (module.path.length > 0) {
        groupInstanceData = null;
    }
    // These only get populated by the top level of a group; staff modules or ungrouped will get nulls here.
    const { moduleProps, staffOverrides } = getModuleProps(core, module);
    if (core.coreType === 'group') {
        groupInstanceData = {
            groupId: core.id,
            groupModuleId: module.id,
            overrides: staffOverrides ?? null,
        };
    }
    // If overrides exist, see if any are applicable to this module.
    const staffProps = module.groupId
        ? groupInstanceData?.overrides?.[node.id] ?? null
        : null;
    const propsToUse = applyPropsDiff(mergeProps([props, moduleProps]), staffProps);
    // Since we're building up based on family tree, we don't need all this.
    // NEXT: variableResolver(vars, module.variables, core.variables, core.componentFieldData);
    // Unlike modules and cores, components don't need to be cloned, I think.
    // TODO: Add i18n to this.
    // const props = Object.assign(
    //   {},
    //   isJSONObject(core.componentFieldData) ? core.componentFieldData : {},
    //   isJSONObject(core.i18n?.[1]?.data) ? core.i18n?.[1]?.data : {},
    //   isJSONObject(core.i18n?.[0]?.data) ? core.i18n?.[0]?.data : {}
    // );
    // pre-compute these for later.
    const mid = module.id;
    const groupModuleId = (groupInstanceData?.groupModuleId !== mid &&
        groupInstanceData?.groupModuleId) ||
        null;
    const rv = {
        component: component.reactName,
        config: {
            scope: 'attendee',
            attendeeApiEndpoint: `${config.endpointBase}/attendee`,
            legacyEndpoint: config.legacyEndpoint,
            bitmovinKey: config.bitmovinKey,
            streamKey: config.streamKey,
            timestampEndpoint: `${config.endpointBase}/timestamp`,
            livekitEndpoint: config.livekitEndpoint,
        },
        id: computeId({ mid, groupModuleId }),
        mid,
        cid: core.id,
        gid: module.groupId || null,
        groupModuleId,
        path: module.path || [],
        props: propsToUse,
    };
    page.pageModuleLookup[rv.id] = rv;
    node.groupCoreId = rv.gid;
    node.groupModuleId = rv.groupModuleId;
    node.htmlId = rv.id;
    node.slot = module.parentSlotSlug;
    if ('kids' in node && node.kids) {
        rv.slots = {};
        node.kids.forEach((kid) => {
            const km = all.modules[kid.id];
            if (rv.slots && km.parentSlotSlug) {
                let slot = rv.slots[km.parentSlotSlug];
                if (!slot) {
                    slot = rv.slots[km.parentSlotSlug] = [];
                }
                if (Array.isArray(slot)) {
                    slot.push(nodeToPageModule({
                        config,
                        node: kid,
                        page,
                        all,
                        tree,
                        vars,
                        groupInstanceData,
                    }));
                }
            }
        });
    }
    return rv;
}
const setAttributeIfRelevant = (node, attr, value) => {
    if (value) {
        node.setAttribute(attr, value);
    }
};
const makeXmlNode = (document, parent, treeNodes, all, page) => {
    treeNodes.forEach((treeNode) => {
        const xmlNode = document.createElement(treeNode.kind);
        /* Append this xmlNode. We still have a reference to it */
        parent.appendChild(xmlNode);
        xmlNode.id = treeNode.htmlId ?? '';
        xmlNode.setAttribute('coreId', treeNode.cid);
        xmlNode.setAttribute('moduleId', treeNode.id);
        xmlNode.setAttribute('coreType', treeNode.coreType);
        xmlNode.setAttribute('i', treeNode.i.toString());
        setAttributeIfRelevant(xmlNode, 'slot', treeNode.slot);
        setAttributeIfRelevant(xmlNode, 'groupModuleId', treeNode.groupModuleId);
        setAttributeIfRelevant(xmlNode, 'groupCoreId', treeNode.groupCoreId);
        setAttributeIfRelevant(xmlNode, 'path', treeNode.path?.join(','));
        const component = Object.values(all.components).find((x) => x.reactName === treeNode.kind);
        const element = page.pageModuleLookup[xmlNode.id];
        if (!component || !element) {
            return;
        }
        const slots = new Set();
        // Add all the defined slots, whether or not populated with children.
        if (component && component.slotConfiguration) {
            Object.keys(component.slotConfiguration).forEach((x) => slots.add(x));
        }
        if (slots.size > 0) {
            // `DynamicComponent` props are stored as JSON, using them this way should be safe.
            const { layout = null } = element.props;
            let orientation = 'vertical';
            if (isJSONObject(layout) && isOrientation(layout.orientation)) {
                orientation = layout.orientation;
            }
            xmlNode.setAttribute('orientation', orientation);
        }
        if ('kids' in treeNode) {
            treeNode.kids.forEach((kid) => {
                if (kid.slot) {
                    slots.add(kid.slot);
                }
            });
            makeXmlNode(document, xmlNode, treeNode.kids, all, page);
        }
        if (slots.size > 0) {
            xmlNode.setAttribute('slots', Array.from(slots).sort().join(','));
        }
    });
};
function buildXmlModel({ page, tree, showId, domainName, all, }) {
    const doc = document.implementation.createDocument(null, 'Root', null);
    // console.log('✨✨', doc, tree, page);
    const root = doc.children.item(0);
    if (root !== null) {
        root.setAttribute('id', 'root');
        root.setAttribute('pathname', page.pathname);
        if (showId) {
            root.setAttribute('showId', showId);
        }
        if (domainName) {
            root.setAttribute('domainName', domainName);
        }
        root.setAttribute('pageId', page.id);
        makeXmlNode(doc, root, tree, all, page);
    }
    return doc;
}
export function buildUpModules({ config, page, showId, domainName, }) {
    const all = {
        components: arrayToRecord(page.allComponents, 'id'),
        cores: arrayToRecord(page.allCores, 'id'),
        modules: arrayToRecord(page.allModules, 'id'),
    };
    const rootModules = page.allModules
        .filter((x) => x.pageIndex !== null)
        .sort((a, b) => (a.pageIndex ?? 0) - (b.pageIndex ?? 0));
    const tree = familyTree(rootModules, all);
    const pageModuleLookup = {};
    const pageWithLookup = { ...page, pageModuleLookup };
    const modules = tree.map((x) => nodeToPageModule({ config, node: x, page: pageWithLookup, all, tree }));
    const structure = buildXmlModel({
        page: pageWithLookup,
        tree,
        showId,
        domainName,
        all,
    });
    return Object.assign({}, pageWithLookup, { modules, structure });
}
